import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Skill } from '../types';
import { 
  SiSolana, SiBitcoin, SiEthereum, SiReact, SiNextdotjs, SiVuedotjs, SiSvelte, SiTailwindcss, SiNodedotjs, SiRust, SiGo, SiPython, SiBnbchain, SiXrp, SiCardano
} from 'react-icons/si';

const Skills: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const skills: Skill[] = [
    // Blockchains
    { id: '1', name: 'Solana', icon: 'solana', category: 'blockchain', level: 'expert' },
    { id: '2', name: 'Bitcoin', icon: 'bitcoin', category: 'blockchain', level: 'advanced' },
    { id: '3', name: 'Ethereum', icon: 'ethereum', category: 'blockchain', level: 'intermediate' },
    { id: '4', name: 'BNB', icon: 'bsc', category: 'blockchain', level: 'expert' },
    { id: '5', name: 'XRPL', icon: 'xrpl', category: 'blockchain', level: 'advanced' },
    { id: '6', name: 'Cardano', icon: 'cardano', category: 'blockchain', level: 'intermediate' },
    { id: '7', name: 'Tron', icon: 'tron', category: 'blockchain', level: 'advanced' },
    { id: '8', name: 'Others', icon: 'ethereum', category: 'blockchain', level: 'intermediate' },
    
    // Frontend Development
    { id: '9', name: 'ReactJS', icon: 'react', category: 'frontend', level: 'expert' },
    { id: '10', name: 'NextJS', icon: 'next', category: 'frontend', level: 'intermediate' },
    { id: '11', name: 'VueJS', icon: 'vue', category: 'frontend', level: 'advanced' },
    { id: '12', name: 'SvelteJS', icon: 'svelte', category: 'frontend', level: 'expert' },
    { id: '13', name: 'Tailwind', icon: 'tailwind', category: 'frontend', level: 'intermediate' },
    { id: '14', name: 'Others', icon: 'react', category: 'frontend', level: 'advanced' },
    
    // Backend Development
    { id: '15', name: 'NodeJS', icon: 'node', category: 'tools', level: 'advanced' },
    { id: '16', name: 'Rust', icon: 'rust', category: 'tools', level: 'intermediate' },
    { id: '17', name: 'GoLang', icon: 'go', category: 'tools', level: 'expert' },
    { id: '18', name: 'Python', icon: 'python', category: 'tools', level: 'advanced' },
    { id: '19', name: 'Others', icon: 'node', category: 'tools', level: 'intermediate' },

  ];

  const skillCategories = [
    {
      title: 'Blockchains',
      skills: skills.filter(skill => skill.category === 'blockchain'),
      color: '#00d4ff'
    },
    {
      title: 'Frontend Development',
      skills: skills.filter(skill => skill.category === 'frontend'),
      color: '#ffd700'
    },
    {
      title: 'Backend Development',
      skills: skills.filter(skill => skill.category === 'tools'),
      color: '#8a2be2'
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const categoryVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const skillVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'expert': return '#00ff88';
      case 'advanced': return '#00d4ff';
      case 'intermediate': return '#ffd700';
      case 'beginner': return '#ff6b6b';
      default: return '#ffffff';
    }
  };

  const renderIcon = (icon: string) => {
    switch (icon) {
      case 'solana': return <SiSolana />;
      case 'bitcoin': return <SiBitcoin />;
      case 'ethereum': return <SiEthereum />;
      case 'tron': return <span style={{ fontWeight: 'bold' }}>TRX</span>;
      case 'react': return <SiReact />;
      case 'next': return <SiNextdotjs />;
      case 'vue': return <SiVuedotjs />;
      case 'svelte': return <SiSvelte />;
      case 'tailwind': return <SiTailwindcss />;
      case 'node': return <SiNodedotjs />;
      case 'rust': return <SiRust />;
      case 'go': return <SiGo />;
      case 'python': return <SiPython />;
      case 'bsc': return <SiBnbchain />;
      case 'xrpl': return <SiXrp />;
      case 'cardano': return <SiCardano />;
      default: return <SiReact />;
    }
  };

  return (
    <section id="skills" className="skills" ref={ref}>
      <div className="container">
        <motion.h2
          className="section-title"
          initial={{ opacity: 0, y: -20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ duration: 0.6 }}
        >
          Technical Skills
        </motion.h2>

        <motion.div
          className="skills-grid"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {skillCategories.map((category, _categoryIndex) => (
            <motion.div
              key={category.title}
              className="skill-category"
              variants={categoryVariants}
              whileHover={{ 
                scale: 1.02,
                boxShadow: `0 10px 30px ${category.color}20`
              }}
              style={{ '--category-color': category.color } as React.CSSProperties}
            >
              <h3 style={{ color: category.color }}>{category.title}</h3>
              <div className="skill-items">
                {category.skills.map((skill, _skillIndex) => (
                  <motion.div
                    key={skill.id}
                    className="skill-item"
                    variants={skillVariants}
                    whileHover={{ 
                      scale: 1.05,
                      backgroundColor: `${category.color}20`
                    }}
                    style={{
                      borderLeft: `3px solid ${getLevelColor(skill.level)}`
                    }}
                  >
                    <span style={{ color: category.color, fontSize: 18, display: 'inline-flex' }}>
                      {renderIcon(skill.icon)}
                    </span>
                    <span>{skill.name}</span>
                    <div 
                      className="skill-level"
                      style={{ backgroundColor: getLevelColor(skill.level) }}
                    />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;

